import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import { toast } from "react-toastify";

import api from "../../services/api";
import ProductImagePicker from "./ProductImagePicker";

const INITIAL_FORM = {
  title: "",
  slug: "",
  categoryCode: "",
  type: "FOOD",
  description: "",
  sortOrder: 0,
  status: "ACTIVE",
  image: "",
  unit: "عدد",
};

const PRODUCT_TYPES = [
  {
    value: "FOOD",
    label: "غذایی",
  },
  {
    value: "PHYSICAL",
    label: "کالای فیزیکی",
  },
  {
    value: "SERVICE",
    label: "خدماتی",
  },
  {
    value: "DIGITAL",
    label: "دیجیتال",
  },
];

const PRODUCT_STATUSES = [
  {
    value: "ACTIVE",
    label: "فعال",
  },
  {
    value: "INACTIVE",
    label: "غیرفعال",
  },
  {
    value: "ARCHIVED",
    label: "آرشیو شده",
  },
];

const ProductFormModal = ({
  show,
  onHide,
  product,
  categories,
  onSuccess,
}) => {
  const [form, setForm] =
    useState(INITIAL_FORM);

  const [saving, setSaving] =
    useState(false);

  const [showImagePicker, setShowImagePicker] =
    useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (product) {
      setForm({
        title: product.title || "",
        slug: product.slug || "",
        categoryCode:
          product.category?.code ||
          product.categoryCode ||
          "",
        type:
          product.type || "FOOD",
        description:
          product.description || "",
        sortOrder:
          product.sortOrder ?? 0,
        status:
          product.status || "ACTIVE",
        image:
          product.image || "",
        unit:
          product.unit || "عدد",
      });
    } else {
      setForm({
        ...INITIAL_FORM,
      });
    }
  }, [show, product]);

  const updateField = (
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error(
        "عنوان محصول را وارد کنید."
      );
      return;
    }

    if (!form.slug.trim()) {
      toast.error(
        "Slug محصول را وارد کنید."
      );
      return;
    }

    if (!form.categoryCode) {
      toast.error(
        "دسته محصول را انتخاب کنید."
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: form.title.trim(),

        slug: form.slug.trim(),

        categoryCode:
          form.categoryCode,

        type: form.type,

        description:
          form.description.trim() ||
          undefined,

        sortOrder:
          Number(form.sortOrder) || 0,

        status: form.status,

        image:
          form.image.trim() ||
          undefined,

        unit:
          form.unit.trim() ||
          "عدد",
      };

      if (product?.code) {
        await api.patch(
          `/admin/products/${product.code}`,
          payload
        );

        toast.success(
          "محصول با موفقیت ویرایش شد."
        );
      } else {
        await api.post(
          "/admin/products",
          payload
        );

        toast.success(
          "محصول با موفقیت اضافه شد."
        );
      }

      onHide();
      await onSuccess();
    } catch (error) {
      const message =
        error.response?.data
          ?.message;

      toast.error(
        Array.isArray(message)
          ? message.join("، ")
          : message ||
              "خطا در ذخیره محصول."
      );
    } finally {
      setSaving(false);
    }
  };

  const imagePreview = form.image
    ? `/${form.image.replace(
        /^\/+/,
        ""
      )}`
    : "/images/product-1.jpg";

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        backdrop="static"
      >
        <Form
          onSubmit={handleSubmit}
        >
          <Modal.Header
            closeButton
          >
            <Modal.Title>
              {product
                ? "ویرایش محصول"
                : "افزودن محصول"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    عنوان محصول
                  </Form.Label>

                  <Form.Control
                    required
                    value={form.title}
                    onChange={(event) =>
                      updateField(
                        "title",
                        event.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Slug
                  </Form.Label>

                  <Form.Control
                    required
                    value={form.slug}
                    onChange={(event) =>
                      updateField(
                        "slug",
                        event.target.value
                      )
                    }
                    dir="ltr"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    دسته محصول
                  </Form.Label>

                  <Form.Select
                    required
                    value={
                      form.categoryCode
                    }
                    onChange={(event) =>
                      updateField(
                        "categoryCode",
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      انتخاب دسته...
                    </option>

                    {categories.map(
                      (category) => (
                        <option
                          key={
                            category.code
                          }
                          value={
                            category.code
                          }
                        >
                          {category.title}
                        </option>
                      )
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    نوع محصول
                  </Form.Label>

                  <Form.Select
                    value={form.type}
                    onChange={(event) =>
                      updateField(
                        "type",
                        event.target.value
                      )
                    }
                  >
                    {PRODUCT_TYPES.map(
                      (item) => (
                        <option
                          key={
                            item.value
                          }
                          value={
                            item.value
                          }
                        >
                          {item.label}
                        </option>
                      )
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    وضعیت
                  </Form.Label>

                  <Form.Select
                    value={form.status}
                    onChange={(event) =>
                      updateField(
                        "status",
                        event.target.value
                      )
                    }
                  >
                    {PRODUCT_STATUSES.map(
                      (item) => (
                        <option
                          key={
                            item.value
                          }
                          value={
                            item.value
                          }
                        >
                          {item.label}
                        </option>
                      )
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    ترتیب نمایش
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min="0"
                    value={
                      form.sortOrder
                    }
                    onChange={(event) =>
                      updateField(
                        "sortOrder",
                        event.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    واحد
                  </Form.Label>

                  <Form.Control
                    value={form.unit}
                    onChange={(event) =>
                      updateField(
                        "unit",
                        event.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    تصویر محصول
                  </Form.Label>

                  <div className="d-flex gap-2">
                    <Form.Control
                      value={form.image}
                      onChange={(event) =>
                        updateField(
                          "image",
                          event.target.value
                        )
                      }
                      placeholder="images/products/restaurent/kebab.jpg"
                      dir="ltr"
                    />

                    <Button
                      type="button"
                      variant="outline-primary"
                      className="text-nowrap"
                      onClick={() =>
                        setShowImagePicker(
                          true
                        )
                      }
                    >
                      انتخاب تصویر
                    </Button>
                  </div>

                  <Form.Text className="text-muted">
                    فقط آدرس تصویر در دیتابیس
                    ذخیره می‌شود.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    پیش‌نمایش تصویر
                  </Form.Label>

                  <div
                    className="border rounded bg-light d-flex align-items-center justify-content-center"
                    style={{
                      height: 180,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(event) => {
                        event.currentTarget.src =
                          "/images/product-1.jpg";
                      }}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    توضیحات
                  </Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={
                      form.description
                    }
                    onChange={(event) =>
                      updateField(
                        "description",
                        event.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={onHide}
              disabled={saving}
            >
              انصراف
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving
                ? "در حال ذخیره..."
                : "ذخیره محصول"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ProductImagePicker
        show={showImagePicker}
        onHide={() =>
          setShowImagePicker(false)
        }
        value={form.image}
        onSelect={(image) =>
          updateField(
            "image",
            image
          )
        }
      />
    </>
  );
};

export default ProductFormModal;